import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { LIMITS, cleanTag, displayTag, gestureCount, loneSticker, type ChatImage, type Member } from '@engineer-os/chat';
import { metaFor, sameDay } from '../../lib/chat';
import { clockTime, dayLabel, dayLabelWithYear } from '../../lib/format';
import { useStrings } from '../../state/useLocale';
import { Machine, Stars } from '../../ui';
import type { ToastFn } from '../../App';
import type { ChatRow, ChatStatus } from '../../state/useChat';
import { RichText } from './CustomEmoji';
import { Identity } from './Identity';
import { ImageView, MessageImage } from './ImageView';
import { MessageMenu } from './MessageMenu';
import { splitMentions } from './mentions';

/**
 * The conversation, oldest at the top.
 *
 * Scrolling is the whole difficulty of a message list. The rule here: the
 * list follows new messages only if you were already at the bottom (within
 * about 40px), so reading something older is never interrupted; your own
 * sends always bring you down, because you just typed them; and a page of
 * earlier messages arriving at the top keeps what you were looking at where
 * it was, by adding exactly the height that was inserted above it.
 *
 * Rows from the same tag within five minutes share one header, so a burst
 * of short lines reads as one turn. A hairline with the day on it separates
 * days, and also ends a group: a line sent after midnight is a new turn.
 *
 * State without hue, like everything else: your own rows are bolder, a
 * pending row is dimmer, and a failed one says so in words next to a retry.
 * The faces down the left edge and the emoji in the text are the one thing
 * here allowed colour, because they are not interface.
 * Text is rendered as text. Nothing that arrives on the socket is ever
 * interpreted as markup — the one exception is a shortcode that exactly
 * matches one of the emoji this product drew for itself, which becomes that
 * drawing and nothing else (CustomEmoji.tsx). There is no path from a message
 * body to arbitrary HTML, and a shortcode nobody drew stays the text it is.
 * That rule is the reason mentions are highlighted by cutting the string into
 * React nodes (mentions.ts) rather than by wrapping a match in a span of HTML:
 * a highlighter that built markup out of a message body would have opened
 * exactly the hole the rest of this file exists to keep shut
 * (docs/architecture.md, "Security posture").
 *
 * There is no `aria-live` region here. A room can carry a line every few
 * seconds, and a screen reader that speaks each one over the terminal's
 * own output makes both unusable; the unread count on the toggle's label is
 * what a screen reader gets, and the list is read when it is focused.
 *
 * ## The five kinds of row
 *
 * A message used to be one thing. It is now five, and each of them is a
 * different shape rather than a bubble with a badge on it:
 *
 * - **A message.** What it always was, with four things that may hang off
 *   it: the quote of what it answers, the picture it carries, the reactions
 *   under it, and the mark that says it is pinned. A picture with no caption
 *   is an ordinary message and not a fifth kind — the words are simply the
 *   empty string, and the bubble that would have held them is left out rather
 *   than drawn empty (ImageView.tsx is what draws the picture, in the row and
 *   full size).
 * - **A cleared level.** The world posting into the room. One compact line
 *   with the stars on it, never a bubble, because nobody typed it and a
 *   bubble is the shape of somebody talking.
 * - **A message with nothing in it.** A tombstone. The row still arrives from
 *   the room with an empty body, and it is drawn rather than dropped so that a
 *   conversation somebody has taken something out of has a visible gap instead
 *   of quietly closing over one — the reply underneath it still has something
 *   to point at. It says which of the two things happened: a moderator took it
 *   down, or the person who wrote it took it back. Same shape, because the gap
 *   is the same gap; different sentence, because they are not.
 * - **A blocked message.** Collapsed to one line with a way to open it. A
 *   block is local to this browser and is about noise, not evidence: nothing
 *   was sent to anybody, and the message is one click away for as long as the
 *   room keeps it.
 * - **A message you were just sent to.** Any of the above, flashed once,
 *   because arriving at a permalink and being shown a wall of text with no
 *   idea which line was meant is the same as not arriving.
 */

const GROUP_MS = 5 * 60_000;
const DAY_MS = 86_400_000;
const NEAR_BOTTOM_PX = 40;
/** Up to this many emoji, alone in a message, are drawn large. */
const JUMBO_MAX = 6;

/**
 * The hairline between two days: "Today", "Yesterday", then a date.
 *
 * The two named days are words and come from the catalogue; the date is a
 * date and comes from lib/format, which is one `Intl.DateTimeFormat` per
 * language with the numbering system pinned to Western digits. The year
 * appears only once it differs from this one, which is the same rule
 * `dayLabel` and `dayLabelWithYear` exist to express.
 *
 * This used to be `formatDay` in lib/chat, next to a hand-rolled `HH:MM`.
 * Both were written before there was a second language and both did their own
 * formatting; lib/format is where that decision lives now, and the clock is
 * pinned to 24 hours there for a reason this list depends on — see the comment
 * on `clockTime`: a column of times that is five characters wide sometimes and
 * eight others makes the whole thread breathe in and out.
 */
function daySeparator(ts: number, now: number, today: string, yesterday: string): string {
  if (sameDay(ts, now)) return today;
  if (sameDay(ts, now - DAY_MS)) return yesterday;
  const d = new Date(ts);
  return d.getFullYear() === new Date(now).getFullYear() ? dayLabel(d) : dayLabelWithYear(d);
}

/** Which message's menu is open, and whether it opened into the palette. */
export interface OpenMenu {
  msg: number;
  reacting: boolean;
}

export interface MessageListProps {
  rows: ChatRow[];
  you: Member | null;
  /** Who is in the room, for resolving the tags a mention carries into names. */
  members: Member[];
  status: ChatStatus;
  hasEarlier: boolean;
  loadingEarlier: boolean;
  onEarlier(): void;
  onRetry(nonce: string): void;
  /** Tags muted in this browser. Their messages collapse. */
  blocked: string[];
  /** The message to flash, because we have just been sent to it. */
  highlight: number | null;
  /** A listener may read the room but not act in it. */
  canPost: boolean;
  /** Pinning is a signed-in member's. */
  canPin: boolean;
  /** The room holds as many pins as the protocol allows. */
  pinsFull: boolean;
  menu: OpenMenu | null;
  onMenu(id: number, reacting: boolean): void;
  onMenuClose(): void;
  onReact(id: number, emoji: string, on: boolean): void;
  onReply(row: ChatRow): void;
  onPin(id: number, on: boolean): void;
  /** Put this message's words back in the composer. The row, not the id: the
   *  composer needs the text it is about to replace. */
  onEdit(row: ChatRow): void;
  /** Ask about deleting it. The row, because the screen that asks quotes it. */
  onDelete(row: ChatRow): void;
  onReport(row: ChatRow): void;
  onBlock(tag: string, on: boolean): void;
  /**
   * Open the card saying who somebody is, from their name on a message.
   *
   * The whole row is handed over rather than the tag, because the row is the
   * only thing that knows anything about a sender who has since left — a
   * message carries its own snapshot of the person who wrote it, and most of
   * the names on screen belong to people the member list no longer has
   * (`whoIs`, lib/chat.ts).
   */
  onWho(row: ChatRow): void;
  /** Go to a message in this room: a reply's parent, or a pin. */
  onGoTo(id: number): void;
  /** The permalink to one message, from lib/chat's `chatLink`. */
  link(id: number): string;
  notify: ToastFn;
}

const rowKey = (r: ChatRow) => r.nonce ?? `m${r.id}`;

export function MessageList({
  rows,
  you,
  members,
  status,
  hasEarlier,
  loadingEarlier,
  onEarlier,
  onRetry,
  blocked,
  highlight,
  canPost,
  canPin,
  pinsFull,
  menu,
  onMenu,
  onMenuClose,
  onReact,
  onReply,
  onPin,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  onWho,
  onGoTo,
  link,
  notify,
}: MessageListProps) {
  const s = useStrings().chat;
  const ref = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const prevFirst = useRef<string | null>(null);
  const prevHeight = useRef(0);
  const prevCount = useRef(0);
  /**
   * Blocked messages opened by hand, by id.
   *
   * Kept here rather than in the preferences, because it is a decision about
   * one message and not about a person: opening one line somebody wrote is
   * not unblocking them, and it should not survive the panel any longer than
   * the curiosity that caused it.
   */
  const [shown, setShown] = useState<number[]>([]);
  /**
   * The picture being looked at full size, or none.
   *
   * The image itself rather than the message's id, because the full-size view
   * needs the shape and the object name and nothing else about the row — and
   * because a room trims as it goes: holding an id would mean the view could
   * be looking at a message that has just scrolled out of the window it is
   * keeping, and then have nothing to draw.
   */
  const [big, setBig] = useState<ChatImage | null>(null);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const first = rows[0] ? rowKey(rows[0]) : null;
    const last = rows[rows.length - 1];
    const prepended =
      prevCount.current > 0 &&
      prevFirst.current !== null &&
      first !== prevFirst.current &&
      rows.some((r) => rowKey(r) === prevFirst.current);

    if (prepended) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (prevCount.current === 0 || nearBottom.current || last?.pending) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
    prevCount.current = rows.length;
  }, [rows]);

  /**
   * Being sent to one message.
   *
   * After the layout effect above, and deliberately: that one has just decided
   * where the list should sit for an ordinary arrival, and this overrules it
   * for the one case where the learner did not arrive, they were sent. It runs
   * on the id and on the rows, because following a permalink is two steps —
   * the id changes, then the page containing it arrives — and the row is only
   * there to scroll to after the second.
   *
   * `block: 'center'` rather than the top: a message with nothing above it is
   * a message with no conversation around it, which is most of what a
   * permalink is for.
   */
  useEffect(() => {
    if (highlight === null) return;
    ref.current?.querySelector<HTMLElement>(`[data-msg="${highlight}"]`)?.scrollIntoView({ block: 'center' });
  }, [highlight, rows]);

  const items: ReactNode[] = [];
  // One reading of the clock for the whole pass, so two separators rendered in
  // the same paint cannot disagree about where midnight is.
  const now = Date.now();
  let prev: ChatRow | null = null;
  for (const row of rows) {
    const newDay = prev === null || !sameDay(prev.ts, row.ts);
    if (newDay) {
      const day = daySeparator(row.ts, now, s.list.today, s.list.yesterday);
      items.push(
        <div key={`d${row.ts}`} className="chat-day" role="separator" aria-label={day}>
          {day}
        </div>,
      );
    }

    const own = you !== null && row.tag === you.tag;
    const flashed = highlight !== null && row.id === highlight;
    const when = new Date(row.ts);

    /* --- The world, rather than somebody ---------------------------------
       A cleared level. One line, the stars, and no bubble: nobody typed it,
       and the shape says so before the words do. */
    if (row.kind === 'cleared') {
      items.push(
        <div
          key={rowKey(row)}
          data-msg={row.id}
          className={flashed ? 'chat-event chat-flash' : 'chat-event'}
        >
          {/* Clickable here too. Nobody typed this line, but somebody
              cleared the level it is about, and they are exactly the person a
              reader of a level room wants to know something about. */}
          <Identity m={row} onOpen={() => onWho(row)} />
          <span className="chat-event-line">{s.list.cleared(row.level ?? 0)}</span>
          {typeof row.stars === 'number' && <Stars value={row.stars} className="chat-event-stars" />}
          <time className="msg-time" dir="ltr" dateTime={when.toISOString()}>
            {clockTime(when)}
          </time>
        </div>,
      );
      // An event ends a group. The next line from the same person is a new
      // turn, because something happened in between.
      prev = null;
      continue;
    }

    /* --- Taken down ------------------------------------------------------ */
    if (row.hidden) {
      items.push(
        <div key={rowKey(row)} data-msg={row.id} className={flashed ? 'chat-tomb chat-flash' : 'chat-tomb'}>
          {/* One shape, two sentences. The gap is the same gap either way —
              the words are gone and a reply still has something to point at —
              but who took them is a different fact about a different person,
              and a room where every retracted typo looked like a moderation
              would be telling a lie about both. The absent case is a room
              running a build from before authors could delete, where the only
              answer there has ever been is the one this falls back to. */}
          {row.hiddenBy === 'author' ? s.list.deleted : s.list.removed}
        </div>,
      );
      prev = null;
      continue;
    }

    /* --- Blocked in this browser ----------------------------------------- */
    if (blocked.includes(row.tag) && !shown.includes(row.id)) {
      items.push(
        <div key={rowKey(row)} data-msg={row.id} className="chat-blocked">
          <span className="label">{s.list.blocked}</span>
          <button type="button" className="chat-link" onClick={() => setShown((was) => [...was, row.id])}>
            {s.list.show}
          </button>
        </div>,
      );
      prev = null;
      continue;
    }

    const grouped =
      !newDay &&
      prev !== null &&
      prev.tag === row.tag &&
      row.ts - prev.ts < GROUP_MS &&
      !prev.failed &&
      // A reply carries a quote above it, and a quote under a shared header
      // would look like part of the message before it.
      !row.replyTo;
    // A reply that is nothing but emoji is a gesture, and a gesture set in
    // body copy reads as a typo. A handful get drawn large; a wall of them
    // stays the size it was, because at that point it is a wall of text.
    // Ours count with Unicode's: `:shipit:` is exactly as much of a gesture
    // as 🎉, and the one kind of emoji this product drew itself would
    // otherwise be the one kind that never gets to be one.
    const jumbo = gestureCount(row.text);
    // A message that *is* a sticker, rather than one with a sticker in it.
    // Asked before `jumbo` is used rather than folded into it, because they
    // answer different questions: `gestureCount` says how many pictures a
    // wordless message holds so a short run can be drawn large, and this says
    // the message has stopped being a line of text at all. A sticker with
    // "nice" after it is a sentence and is set as one.
    const sticker = loneSticker(row.text);
    const meta = metaFor(row.level, row.rank);
    const reactions = row.reactions ?? [];
    // Only the people the message actually addressed, and only those still in
    // the room: what travelled is a tag, and a name that matches nobody the
    // message named is text somebody typed.
    const named = row.mentions && row.mentions.length > 0 ? members.filter((m) => row.mentions!.includes(m.tag)) : [];
    const parts = named.length > 0 ? splitMentions(row.text, named) : null;
    /**
     * Whether there is a bubble of words to draw at all.
     *
     * Somebody dropping a screenshot with nothing to say is an ordinary
     * message whose `text` is the empty string, and that is common rather
     * than a corner. The bubble is left out entirely in that case instead of
     * being drawn empty: `.msg-text` carries `pre-wrap`, a line height and
     * the "edited" footnote, and an empty one under a picture is a blank line
     * the reader has to account for.
     *
     * The footnote is half of the test rather than an afterthought. A message
     * can be edited down to nothing but its picture, and a row that dropped
     * the bubble on emptiness alone would quietly stop saying so.
     *
     * A row with no picture keeps the bubble whatever its text is, so nothing
     * about a text-only conversation changes: an empty body with no image is
     * a tombstone, and that is drawn several branches above.
     */
    const words = row.text !== '' || row.edited !== undefined;
    const className = [
      'msg',
      grouped ? 'is-cont' : '',
      own ? 'is-own' : '',
      row.pending ? 'is-pending' : '',
      row.failed ? 'is-failed' : '',
      row.pinned ? 'is-pinned' : '',
      flashed ? 'chat-flash' : '',
      // Never both. A lone sticker is one of ours and therefore one gesture,
      // so `jumbo` is true of it too — and `is-jumbo` sets a 26px font that
      // the sticker's own `em` size would then be measured against, drawing it
      // at twice what it should be. The sticker wins because it is the more
      // specific answer to the same question.
      sticker ? 'is-sticker' : jumbo > 0 && jumbo <= JUMBO_MAX ? 'is-jumbo' : '',
    ]
      .filter(Boolean)
      .join(' ');

    items.push(
      <div key={rowKey(row)} data-msg={row.id} className={className}>
        {/* What this answers, above it and clickable. The quote came down the
            wire with the reply — the room copies it on write — so it still
            renders after the message it quotes has been trimmed away, which
            is the entire reason replies are denormalised. */}
        {row.replyTo && (
          <div className="chat-reply">
            <button type="button" className="chat-reply-quote" title={s.reply.quote} onClick={() => onGoTo(row.replyTo!.id)}>
              <span className="msg-name" dir="auto">
                {row.replyTo.name}
              </span>
              <span className="chat-reply-text" dir="auto">
                <RichText text={row.replyTo.text} />
              </span>
            </button>
          </div>
        )}

        {!grouped && (
          <div className="msg-head">
            <Identity m={row} onOpen={() => onWho(row)} />
            {/* The tag, the level-and-rank and the clock are the machine's,
                and they read left to right in both languages: a time laid out
                right to left is a time nobody can match against the terminal
                (docs/i18n.md §7). */}
            <Machine className="msg-tag">#{displayTag(row.tag)}</Machine>
            {meta && <Machine className="msg-meta">{meta}</Machine>}
            <time className="msg-time" dir="ltr" dateTime={when.toISOString()}>
              {clockTime(when)}
            </time>
            {row.pinned && (
              <span className="msg-pin" title={s.list.pinned} aria-label={s.list.pinned}>
                <svg viewBox="0 0 12 12" aria-hidden="true" width="9" height="9">
                  <path d="M4.2 1.6h3.6l-.5 2.9 1.9 1.7v.8H2.8v-.8l1.9-1.7z" fill="currentColor" />
                  <path d="M6 7.4v3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
                </svg>
              </span>
            )}
          </div>
        )}

        {/* The one place in the panel where the direction is the writer's.
            An Arabic line in an English room and an English line in an Arabic
            room each have to read correctly, and neither of them knows which
            room it will be read in — so the bubble takes its direction from
            its first strong character and isolates itself from the thread
            around it. Anything inside it that is machine text is a `<code>`,
            which the stylesheet isolates on its own (locale.css). */}
        {(words || !row.image) && (
          <div className="msg-text" dir="auto">
            {parts === null ? (
              <RichText text={row.text} />
            ) : (
              parts.map((part, i) =>
                'text' in part ? (
                  <RichText key={i} text={part.text} />
                ) : (
                  <span
                    key={i}
                    className={part.run.member.tag === you?.tag ? 'chat-mention chat-mention-me' : 'chat-mention'}
                    title={part.run.member.tag === you?.tag ? s.mention.you : undefined}
                  >
                    {row.text.slice(part.run.at, part.run.at + part.run.length)}
                  </span>
                ),
              )
            )}
            {/* The footnote that says these are not the words that were sent.
                Inside the bubble and after the text rather than beside the
                timestamp in the header, because it is a fact about this line and
                not about the turn — a group of five messages from one person
                shares one header, and a marker up there could not say which of
                the five had changed.

                The time is in the `title` rather than on screen. The question
                most readers have is only whether it changed at all; the one who
                wants to know whether it changed after they answered it can
                hover, and in a 280px panel that is the right way round. */}
            {row.edited !== undefined && (
              <span className="msg-edited" title={s.list.editedAt(clockTime(new Date(row.edited)))}>
                {s.list.edited}
              </span>
            )}
          </div>
        )}

        {/* The picture, under the words and over the reactions.

            Under the words because in a room the words are the message and the
            picture is what it carries: "this is what I get" reads in that
            order, and a caption that arrived above its picture would put the
            sentence a turn away from the name that is already at the top of
            the row. Over the reactions because the reactions are the row's
            footer — they and the `+` that adds to them have to stay the last
            thing, or agreeing with a message means reaching past it. And below
            the quote, which is context for the whole row and not for one part
            of it.

            Keyed on the object name, which is what resets the component's own
            "this one 404ed" flag when the picture on a row actually changes.
            Without it React would keep the instance and the new picture would
            inherit the old one's failure.

            Nothing is drawn here for a row that was taken down or is blocked:
            both are answered several branches above and never reach this
            point, which is the correct answer for both — a deleted message's
            picture is not a thing to keep showing, and a blocked person's
            picture is exactly the noise a block is about. */}
        {row.image && <MessageImage key={row.image.id} image={row.image} onOpen={() => setBig(row.image!)} />}

        {/* Who agreed, and the cheapest way to agree with them. The buckets
            carry the tags rather than a count, so the one question a reader
            actually has — did I already — is answered without asking the
            server anything. */}
        {reactions.length > 0 && (
          <div className="chat-reactions" role="group" aria-label={s.reactions.label}>
            {reactions.map((bucket) => {
              const mine = you !== null && bucket.tags.includes(you.tag);
              return (
                <button
                  key={bucket.emoji}
                  type="button"
                  className={mine ? 'chat-reaction chat-reaction-mine' : 'chat-reaction'}
                  aria-pressed={mine}
                  disabled={!canPost}
                  onClick={() => onReact(row.id, bucket.emoji, !mine)}
                >
                  <RichText text={bucket.emoji} />
                  <span aria-hidden="true">{bucket.tags.length}</span>
                  <span className="sr-only">{s.reactions.count(bucket.tags.length)}</span>
                </button>
              );
            })}
            {canPost && (
              <button
                type="button"
                className="chat-reaction is-add"
                aria-label={s.reactions.add}
                title={reactions.length >= LIMITS.reaction.perMessage ? s.reactions.full : s.reactions.add}
                disabled={reactions.length >= LIMITS.reaction.perMessage}
                onClick={() => onMenu(row.id, true)}
              >
                {/* A mark rather than a word, and hidden from the reader
                    because the button already has one: "Add a reaction". */}
                <span aria-hidden="true">+</span>
              </button>
            )}
          </div>
        )}

        {row.failed && row.nonce && (
          <div className="msg-failed">
            <span>{s.list.notSent}</span>
            <button type="button" className="chat-link" disabled={status !== 'open'} onClick={() => onRetry(row.nonce!)}>
              {s.list.retry}
            </button>
          </div>
        )}

        {/* A row the server has not numbered yet has no id to act on: no
            permalink, nothing to pin, nothing to react to. The menu appears
            when the echo does, which is a fraction of a second. */}
        {row.id > 0 && (
          <MessageMenu
            id={row.id}
            own={own}
            open={menu?.msg === row.id}
            reacting={menu?.msg === row.id && menu.reacting}
            pinned={row.pinned === true}
            blocked={blocked.includes(row.tag)}
            canBlock={cleanTag(row.tag) !== null}
            canPost={canPost}
            canPin={canPin}
            pinsFull={pinsFull}
            // A listener has no composer to rewrite anything in, so Edit is
            // offered and disabled rather than hidden. Deleting is not gated
            // on it: the menu offers that on your own message whatever the
            // room now thinks you are, and the room decides by tag.
            //
            // A `cleared` row is nobody's to rewrite either — the world
            // announced it and the server vouched for it — and it is not
            // tested here because it cannot reach here: that kind is drawn as
            // an event several branches above and never grows a menu. The room
            // refuses it regardless, which is where the rule belongs.
            canEdit={own && canPost}
            link={link(row.id)}
            onOpen={(reacting) => onMenu(row.id, reacting)}
            onClose={onMenuClose}
            onReact={(emoji) => onReact(row.id, emoji, true)}
            onReply={() => onReply(row)}
            onPin={(on) => onPin(row.id, on)}
            onEdit={() => onEdit(row)}
            onDelete={() => onDelete(row)}
            onReport={() => onReport(row)}
            onBlock={(on) => onBlock(row.tag, on)}
            notify={notify}
          />
        )}
      </div>,
    );
    prev = row;
  }

  return (
    <div ref={ref} className="chat-list scroll" role="region" aria-label={s.list.label} tabIndex={0} onScroll={onScroll}>
      {hasEarlier && (
        <button
          type="button"
          className="btn btn-quiet chat-earlier"
          disabled={loadingEarlier || status !== 'open'}
          onClick={onEarlier}
        >
          {loadingEarlier ? s.list.loadingEarlier : s.list.earlier}
        </button>
      )}
      {rows.length === 0 && status === 'open' && <p className="chat-empty">{s.list.empty}</p>}
      {items}
      {/* Written here and drawn somewhere else. It is a child of the list in
          the component tree — which is what lets it close with the room and
          keeps the state that opened it next to the rows that open it — and a
          child of `<body>` in the document, because the drawer is a stacking
          context that nothing inside it can escape. ImageView.tsx says why at
          length. */}
      {big && <ImageView image={big} onClose={() => setBig(null)} />}
    </div>
  );
}

export default MessageList;
